import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import classes from 'components/Dashboards/Project/Components/VersionCompare/style/Table.module.scss';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Tabs } from 'components/DesignSystem/Tabs';
import { useActivitiesPredecessorsLogic } from 'components/widgets/ActivityCompletionPlan/ActivitiesPredecessors.logic';
import activitiesPredecessorsStyle from 'components/widgets/ActivityCompletionPlan/ActivitiesPredecessors.module.scss';
import { useActivitiesPredecessorsProvider } from 'components/widgets/ActivityCompletionPlan/ActivitiesPredecessors.provider';
import {
    IActivityCompletionList,
    TKeys,
    columnsV8,
    initialState,
} from 'components/widgets/ActivityCompletionPlan/ActivitiesPredecessors.utils';
import { FragnetSummary } from 'components/widgets/ActivityCompletionPlan/components/FragnetSummary/FragnetSummary';
import { SortFilterPanel } from 'components/widgets/ActivityCompletionPlan/components/SortFilterPanel/SortFilterPanel';
import classesGeneralWidget from 'components/widgets/Utils/Styles/widget.module.scss';

const { componentKey, id, title: defaultTitle, endpoint } = widgetsConfigurations.activityPredecessors;

export const ActivitiesPredecessors = ({
    contractId,
    latestVersionId,
    projectId,
    widgetId,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    globalFilterData,
    hashcode,
    localFilters,
    compareVersionId,

    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = defaultTitle,
    setIsPayloadReady,
}: IComponentProps) => {
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
        widgetViewMode,
    });

    const { ui, api } = useActivitiesPredecessorsProvider({
        widgetId,
        latestVersionId,
        localFilters,
        globalFilterData,
        compareVersionId,
        componentKey,
        contractId,
        table,
        widgetViewMode,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
        apiPayload: api.getActivityCompletionPlan.payload,
        widgetEndpoint: endpoint,
    });

    const { logic } = useActivitiesPredecessorsLogic({
        ui,
        api,
        title,
        subTitle,
        latestVersionId,
        table,
        setIsPayloadReady,
        widgetViewMode,
    });

    const isInReport = WidgetViewModeUtils.isReportView(widgetViewMode);

    return (
        <WidgetWithTitle
            id={id}
            title={<WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />}
            className={isInReport ? widgetWithTitleClasses.inReport : ''}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                    shouldHide={true}
                />,
                <AddToReport
                    key={'addWidgetToReport'}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    projectId={projectId}
                    contractId={contractId}
                    uiState={ui.state}
                    shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                    callBack={table.setWidgetColumns}
                    apiPayload={api.getActivityCompletionPlan.payload}
                    widgetEndpoint={endpoint}
                />,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ExportDropdown
                        key={'export'}
                        title={title}
                        subTitle={subTitle}
                        activeTabTitle={ui.state.activeTab}
                        handleAPICsvExport={logic.handleAPICsvExport}
                    />
                ),
                !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                    <ColumnsManager
                        onOpenModal={() => table.setModalColumnsOpen(true)}
                        onHideModal={() => table.setModalColumnsOpen(false)}
                        showModal={table.modalColumnsOpen}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        tableInstance={table.tableInstance}
                        initialState={initialState}
                        columns={columnsV8}
                        onChangeOrder={table.setColumnOrder}
                        onChangeVisibility={table.setColumnVisibility}
                        widgetViewMode={widgetViewMode}
                    />
                ),
                <TooltipIcon key={'info'} tooltip={api.getActivityCompletionPlan.instance.data?.info} />,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ThreeDotsMenu
                        widgetViewMode={widgetViewMode}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={title}
                        shareLink={route}
                        projectId={projectId}
                        contractId={contractId}
                    />
                ),
            ]}
            titleFilters={[
                <ReportingVersionAndPills
                    key={'ReportingVersionAndPills'}
                    selectedProjectAndContract={widgetSelectedProjectAndContract}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
                <Narrative key={'narrative'} editNarrative={editNarrative} />,
                <PinboardPills
                    key={'pinboardPills'}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                    isInPinboard={ui.isInPinboard}
                />,
            ]}
        >
            <TableContainer className={classes.tableWrapperContainer}>
                {logic.tabsArrData.length > 0 && api.getActivityCompletionPlan.instance.data && (
                    <div className={activitiesPredecessorsStyle.fragnetSummaryWrapper}>
                        <FragnetSummary data={api.getActivityCompletionPlan.instance.data} />
                    </div>
                )}
                {!api.getActivityCompletionPlan.instance.isLoading && !widgetId && logic.tabsArrData.length > 0 && (
                    <SortFilterPanel taskVersionHashCode={ui.state.milestoneFragnet} hashcode={hashcode} />
                )}
                {logic.tabsArrData.length > 0 && (
                    <Tabs
                        tabs={logic.tabsArrData}
                        value={ui.state.activeTab}
                        onChange={(value) => ui.setters.onSetActiveTab(value as TKeys)}
                    />
                )}
                <TableV8<IActivityCompletionList>
                    data={api.getActivityCompletionPlan.instance.data?.activity_completion_list || []}
                    columns={columnsV8}
                    isLoading={
                        api.getActivityCompletionPlan.instance.isLoading ||
                        api.getActivityCompletionPlan.instance.isFetching
                    }
                    noData={
                        <NoData>
                            To view Completion Plan for the <strong>Project Finish</strong> activity select the{' '}
                            <strong>first</strong> activity in fragnet filter list above.
                            <br />
                            To view Completion Plan for a <strong>specific</strong> milestone/activity select it from
                            the fragnet filter list above.
                        </NoData>
                    }
                    sorting={table.sorting}
                    setSorting={table.setSorting}
                    columnVisibility={table.columnVisibility}
                    setColumnVisibility={table.setColumnVisibility}
                    columnOrder={table.columnOrder}
                    setColumnOrder={table.setColumnOrder}
                    rowSelection={table.rowSelection}
                    setRowSelection={table.setRowSelection}
                    totalPages={table.totalPages}
                    pagination={table.pagination}
                    setPagination={table.setPagination}
                    isPaginationDisabled={api.getActivityCompletionPlan.instance.isFetching}
                    displayPaginationBottomBar
                    tableClassName={
                        isInReport
                            ? classesGeneralWidget.reportTableScrollable
                            : classesGeneralWidget.pageTableScrollable
                    }
                    wrapperClassName={
                        WidgetViewModeUtils.isInAnyReportMode(widgetViewMode)
                            ? classesGeneralWidget.reportTableWrapper
                            : ''
                    }
                />
            </TableContainer>
        </WidgetWithTitle>
    );
};
